import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import {
  getLanguageOptions,
  getRoleOptions,
  getTitleOptions,
} from '@/constants/contractPartnerOptions';
import { useContractPartnerForm } from '@/hooks/useContractPartnerForm';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { ContractPartnerDialogProps } from '@/types';
import { Save, X } from 'lucide-react';
import { ContractPartnerFormFields } from './ContractPartnerFormFields';

/**
 * Contract Partner Form Dialog Component
 * Handles contract partner creation and editing in a modal dialog
 *
 * Refactored following best practices:
 * - Single Responsibility: UI concerns only, form logic extracted to custom hook
 * - DRY: Reusable form options moved to constants
 * - Declarative: Uses React Hook Form with proper validation
 * - KISS: Simple, clean component structure
 */
export default function ContractPartnerDialog({
  open,
  onOpenChange,
  contractPartner,
  onSave,
  isLoading = false,
}: ContractPartnerDialogProps) {
  const { t: tForms } = useFormsTranslation();

  // Custom hook handles all form logic (Single Responsibility)
  const { form, handleSubmit, isEditing } = useContractPartnerForm({
    contractPartner,
    onSave,
    isOpen: open,
    tForms,
  });

  // Reusable form options (DRY principle)
  const languageOptions = getLanguageOptions(tForms);
  const roleOptions = getRoleOptions(tForms);
  const titleOptions = getTitleOptions(tForms);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-3xl max-w-4xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? tForms('contractPartner.dialog.editTitle')
              : tForms('contractPartner.dialog.addTitle')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? tForms('contractPartner.dialog.editDescription')
              : tForms('contractPartner.dialog.addDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='space-y-6'
          >
            <ContractPartnerFormFields
              form={form}
              tForms={tForms}
              languageOptions={languageOptions}
              roleOptions={roleOptions}
              titleOptions={titleOptions}
            />

            <DialogFooter className='flex gap-2 pt-6'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className='flex items-center gap-2'
              >
                <X className='h-4 w-4' />
                {tForms('contractPartner.formButtons.cancel') || 'Cancel'}
              </Button>
              <Button
                type='submit'
                disabled={isLoading}
                className='flex items-center gap-2'
              >
                <Save className='h-4 w-4' />
                {isLoading
                  ? tForms('contractPartner.formButtons.saving') || 'Saving...'
                  : isEditing
                    ? tForms('contractPartner.formButtons.update') || 'Update'
                    : tForms('contractPartner.formButtons.save') || 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
